import React from "react";

/**
 * Creative active/inactive indicator for the "main text" state.
 *
 * Renders a pill-shaped toggle with a sliding power-button knob:
 *  - active   -> gold gradient track, knob slid to the right, power glyph lit
 *  - inactive -> muted track, knob slid to the left, power glyph dimmed
 *
 * When `onClick` is provided the toggle is interactive (TextModelForm).
 * When it is omitted the toggle is a read-only state badge (TextModelItem).
 */

// module-level counter to keep SVG gradient/filter ids unique per instance
let uid = 0;

class MainToggle extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hovered: false };
    this.id = `mt-${(uid += 1)}`;
  }

  render() {
    const { active, onClick, label, padding } = this.props;
    const { hovered } = this.state;
    const interactive = typeof onClick === "function";
    const knobX = active ? 25 : 11;

    const trackId = `${this.id}-track`;
    const knobId = `${this.id}-knob`;

    // soft tinted chip: green when active, neutral when inactive
    const baseBg = active
      ? "rgba(34, 197, 94, 0.12)"
      : "rgba(60, 78, 100, 0.05)";
    const hoverBg = active
      ? "rgba(34, 197, 94, 0.20)"
      : "rgba(60, 78, 100, 0.10)";

    return (
      <span
        onClick={onClick}
        onMouseEnter={
          interactive ? () => this.setState({ hovered: true }) : undefined
        }
        onMouseLeave={
          interactive ? () => this.setState({ hovered: false }) : undefined
        }
        role={interactive ? "button" : "img"}
        aria-pressed={interactive ? !!active : undefined}
        aria-label={label}
        title={label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          padding: padding || "1px 4px 3px 0px",
          borderRadius: "14px",
          background: hovered ? hoverBg : baseBg,
          border: `1px solid ${
            active ? "rgba(34, 197, 94, 0.35)" : "rgba(60, 78, 100, 0.10)"
          }`,
          boxShadow: active
            ? "0 1px 3px rgba(34, 197, 94, 0.25)"
            : "0 1px 2px rgba(60, 78, 100, 0.06)",
          cursor: interactive ? "pointer" : "default",
          userSelect: "none",
          transition:
            "background .2s ease, box-shadow .2s ease, border .2s ease",
        }}
      >
        {label && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              color: active ? "#3C4E64" : "#9AA7B5",
              fontSize: "9.5px",
              fontWeight: 600,
              letterSpacing: "0.3px",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              transition: "color .2s ease",
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: active ? "#22C55E" : "#C7D0DA",
                boxShadow: active
                  ? "0 0 0 2.5px rgba(34, 197, 94, 0.2)"
                  : "none",
                transition: "background .2s ease, box-shadow .2s ease",
              }}
            />
            {label}
          </span>
        )}
        <svg
          width="36"
          height="20"
          viewBox="0 0 36 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id={trackId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={active ? "#34D07F" : "#F1F4F8"} />
              <stop offset="1" stopColor={active ? "#16A063" : "#E4E9EF"} />
            </linearGradient>
            <radialGradient id={knobId} cx="0.5" cy="0.35" r="0.75">
              <stop offset="0" stopColor="#FFFFFF" />
              <stop offset="1" stopColor="#EEF2F6" />
            </radialGradient>
          </defs>

          <rect
            x="1.25"
            y="1.25"
            width="33.5"
            height="17.5"
            rx="8.75"
            fill={`url(#${trackId})`}
            stroke={active ? "#12925A" : "#D2DAE3"}
            strokeWidth="1"
            style={{ transition: "fill .2s ease, stroke .2s ease" }}
          />

          <g
            style={{ transition: "transform .25s cubic-bezier(.4,.9,.3,1.3)" }}
          >
            {/* soft shadow under the knob */}
            <ellipse
              cx={knobX}
              cy="11.2"
              rx="6.8"
              ry="6.4"
              fill="rgba(31, 45, 61, 0.16)"
              style={{ transition: "cx .25s cubic-bezier(.4,.9,.3,1.3)" }}
            />
            <circle
              cx={knobX}
              cy="10"
              r="6.8"
              fill={`url(#${knobId})`}
              style={{ transition: "cx .25s cubic-bezier(.4,.9,.3,1.3)" }}
            />
            {/* power-button glyph on the knob */}
            <circle
              cx={knobX}
              cy="10"
              r="3.1"
              fill="none"
              stroke={active ? "#16A063" : "#AEB9C5"}
              strokeWidth="1.4"
              style={{
                transition:
                  "cx .25s cubic-bezier(.4,.9,.3,1.3), stroke .2s ease",
              }}
            />
            <line
              x1={knobX}
              y1="6.4"
              x2={knobX}
              y2="10"
              stroke={active ? "#16A063" : "#AEB9C5"}
              strokeWidth="1.4"
              strokeLinecap="round"
              style={{
                transition:
                  "x1 .25s cubic-bezier(.4,.9,.3,1.3), x2 .25s cubic-bezier(.4,.9,.3,1.3), stroke .2s ease",
              }}
            />
          </g>
        </svg>
      </span>
    );
  }
}

export default MainToggle;
